import { StyledSelect } from "../../primitives/styled-select";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { Check, ChevronLeft, Repeat } from "lucide-react";
import * as React from "react";

import { cn } from "../../lib/cn";
import { Popover, PopoverContent, PopoverTrigger } from "../../primitives/popover";
import {
  type RecurrenceEndKind,
  type RecurrenceFreq,
  type RecurrencePresetId,
  type RecurrenceRule,
  WEEKDAY_CODE_ORDER,
  buildRRule,
  matchPreset,
  parseRRule,
  presetRule,
  toYmd,
  weekdayCode,
} from "./recurrence";

/**
 * RecurrenceField — the calendar composer's "Ripetizione" control.
 *
 * A compact trigger chip opens a Popover with Google-style PRESETS computed
 * from the event's start date (non si ripete / ogni giorno / ogni settimana il
 * {gg} / ogni mese il {n} / ogni anno il {data} / feriali) plus a full
 * "Personalizzato…" builder (frequenza · intervallo · giorni · fine:
 * mai/data/dopo N). Emits the single canonical wire value — an RFC 5545 RRULE
 * body string (``""`` = one-off) — that the BE feeds to Google natively and
 * translates to Graph for Outlook. All RRULE shapes it can produce are exactly
 * the ones the BE ``rrule_to_graph_recurrence`` translator understands.
 */
export interface RecurrenceFieldProps {
  /** Current RRULE body (``""`` = does not repeat). */
  readonly value: string;
  readonly onChange: (rrule: string) => void;
  /** Event start — anchors the presets (weekday / day-of-month / month). */
  readonly start: Date | null;
  readonly disabled?: boolean;
  /** Classes for the trigger chip (matches the composer's other pickers). */
  readonly className?: string;
}

const DEFAULT_TRIGGER =
  "inline-flex items-center gap-1.5 rounded-md border border-hairline bg-white/70 px-2.5 py-1 text-[12.5px] text-ink-1 transition-colors hover:border-ando-orange/40 disabled:cursor-not-allowed disabled:opacity-60";

const FREQ_UNITS: readonly RecurrenceFreq[] = ["DAILY", "WEEKLY", "MONTHLY", "YEARLY"];

export const RecurrenceField: React.FC<RecurrenceFieldProps> = ({
  value,
  onChange,
  start,
  disabled,
  className,
}) => {
  const { i18n } = useLingui();
  const [open, setOpen] = React.useState(false);
  const anchor = React.useMemo(() => start ?? new Date(), [start]);
  const selected = matchPreset(value, anchor);
  const [mode, setMode] = React.useState<"presets" | "custom">(
    selected === "custom" ? "custom" : "presets",
  );

  // Localised names for the preset labels.
  const weekdayLong = React.useMemo(
    () => new Intl.DateTimeFormat(i18n.locale, { weekday: "long" }).format(anchor),
    [i18n.locale, anchor],
  );
  const monthLong = React.useMemo(
    () => new Intl.DateTimeFormat(i18n.locale, { month: "long" }).format(anchor),
    [i18n.locale, anchor],
  );
  const narrowDays = React.useMemo(() => {
    const fmt = new Intl.DateTimeFormat(i18n.locale, { weekday: "narrow" });
    // Monday-first week (2026-06-01 is a Monday).
    return WEEKDAY_CODE_ORDER.map((code, i) => ({
      code,
      label: fmt.format(new Date(2026, 5, 1 + i)),
    }));
  }, [i18n.locale]);

  const presetLabel = React.useCallback(
    (id: RecurrencePresetId): string => {
      switch (id) {
        case "none":
          return i18n._(msg`Non si ripete`);
        case "daily":
          return i18n._(msg`Ogni giorno`);
        case "weekly":
          return i18n._(msg`Ogni settimana il ${weekdayLong}`);
        case "weekdays":
          return i18n._(msg`Ogni giorno feriale (lun–ven)`);
        case "monthly":
          return i18n._(msg`Ogni mese il ${anchor.getDate()}`);
        case "yearly":
          return i18n._(msg`Ogni anno il ${anchor.getDate()} ${monthLong}`);
        case "custom":
          return i18n._(msg`Personalizzato…`);
      }
    },
    [i18n, weekdayLong, monthLong, anchor],
  );

  const triggerText = selected === "custom" ? i18n._(msg`Personalizzato`) : presetLabel(selected);

  const choosePreset = (id: RecurrencePresetId) => {
    if (id === "custom") {
      setMode("custom");
      return;
    }
    onChange(presetRule(id, anchor));
    setOpen(false);
  };

  const PRESET_IDS: readonly RecurrencePresetId[] = [
    "none",
    "daily",
    "weekly",
    "weekdays",
    "monthly",
    "yearly",
    "custom",
  ];

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        if (disabled) return;
        setOpen(o);
        if (o) setMode(selected === "custom" ? "custom" : "presets");
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(DEFAULT_TRIGGER, className)}
          aria-label={i18n._(msg`Ripetizione`)}
        >
          <Repeat className="h-3.5 w-3.5 shrink-0 text-ink-3" aria-hidden />
          <span className="truncate">{triggerText}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[260px] p-1.5">
        {mode === "presets" ? (
          <div className="flex flex-col">
            {PRESET_IDS.map((id) => (
              <button
                key={id}
                type="button"
                onClick={() => choosePreset(id)}
                className="flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12.5px] text-ink-1 transition-colors hover:bg-ando-orange/10"
              >
                <span className="flex w-4 shrink-0 justify-center">
                  {selected === id ? (
                    <Check className="h-3.5 w-3.5 text-ando-orange" aria-hidden />
                  ) : null}
                </span>
                <span className="truncate">{presetLabel(id)}</span>
              </button>
            ))}
          </div>
        ) : (
          <CustomBuilder
            value={value}
            anchor={anchor}
            narrowDays={narrowDays}
            onBack={() => setMode("presets")}
            onChange={onChange}
            onDone={() => setOpen(false)}
          />
        )}
      </PopoverContent>
    </Popover>
  );
};
RecurrenceField.displayName = "RecurrenceField";

// ── Custom builder ──────────────────────────────────────────────────────────

const SELECT_CLS =
  "rounded-md border border-hairline bg-white/70 px-1.5 py-1 text-[12px] text-ink-1 focus:outline-none focus:ring-1 focus:ring-ando-orange/50";
const NUM_CLS =
  "w-12 rounded-md border border-hairline bg-white/70 px-1.5 py-1 text-[12px] text-ink-1 tabular-nums focus:outline-none focus:ring-1 focus:ring-ando-orange/50";

/** Seed a custom rule from the current value, or a sensible default (weekly on
 *  the start weekday) so the builder opens on something coherent. */
function seedRule(value: string, anchor: Date): RecurrenceRule {
  const parsed = parseRRule(value);
  if (parsed) return parsed;
  return {
    freq: "WEEKLY",
    interval: 1,
    byDay: [weekdayCode(anchor)],
    byMonthDay: null,
    byMonth: null,
    endKind: "never",
    until: null,
    count: null,
  };
}

const CustomBuilder: React.FC<{
  value: string;
  anchor: Date;
  narrowDays: ReadonlyArray<{ code: string; label: string }>;
  onBack: () => void;
  onChange: (rrule: string) => void;
  onDone: () => void;
}> = ({ value, anchor, narrowDays, onBack, onChange, onDone }) => {
  const { i18n } = useLingui();
  const [rule, setRule] = React.useState<RecurrenceRule>(() => seedRule(value, anchor));

  // Push every edit up live so Confirm always has the current rule.
  const patch = (next: Partial<RecurrenceRule>) => {
    setRule((prev) => {
      const merged = { ...prev, ...next };
      onChange(buildRRule(merged));
      return merged;
    });
  };

  const unitLabel = (f: RecurrenceFreq): string => {
    switch (f) {
      case "DAILY":
        return i18n._(msg`giorni`);
      case "WEEKLY":
        return i18n._(msg`settimane`);
      case "MONTHLY":
        return i18n._(msg`mesi`);
      case "YEARLY":
        return i18n._(msg`anni`);
    }
  };

  const toggleDay = (code: string) => {
    const has = rule.byDay.includes(code);
    const next = has ? rule.byDay.filter((d) => d !== code) : [...rule.byDay, code];
    // Keep at least one day selected for a weekly rule.
    patch({ byDay: next.length > 0 ? next : rule.byDay });
  };

  const setFreq = (freq: RecurrenceFreq) => {
    // Reset the BY* parts to the shape that matches the new frequency so the
    // emitted RRULE stays translatable (the BE reads BYDAY/BYMONTHDAY per freq).
    if (freq === "WEEKLY") {
      patch({ freq, byDay: rule.byDay.length ? rule.byDay : [weekdayCode(anchor)], byMonthDay: null, byMonth: null });
    } else if (freq === "MONTHLY") {
      patch({ freq, byDay: [], byMonthDay: anchor.getDate(), byMonth: null });
    } else if (freq === "YEARLY") {
      patch({ freq, byDay: [], byMonthDay: anchor.getDate(), byMonth: anchor.getMonth() + 1 });
    } else {
      patch({ freq, byDay: [], byMonthDay: null, byMonth: null });
    }
  };

  const setEndKind = (endKind: RecurrenceEndKind) => {
    patch({
      endKind,
      until: endKind === "until" ? (rule.until ?? toYmd(anchor)) : null,
      count: endKind === "count" ? (rule.count ?? 10) : null,
    });
  };

  return (
    <div className="flex flex-col gap-2.5 p-1">
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1 rounded-md px-1 py-0.5 text-[11px] text-ink-3 transition-colors hover:text-ink-1"
        >
          <ChevronLeft className="h-3.5 w-3.5" aria-hidden />
          {i18n._(msg`Preset`)}
        </button>
      </div>

      {/* Frequency + interval */}
      <div className="flex items-center gap-1.5 text-[12px] text-ink-2">
        <span>{i18n._(msg`Ripeti ogni`)}</span>
        <input
          type="number"
          min={1}
          value={rule.interval}
          onChange={(e) => patch({ interval: Math.max(1, Number.parseInt(e.target.value, 10) || 1) })}
          className={NUM_CLS}
          aria-label={i18n._(msg`Intervallo`)}
        />
        <StyledSelect
          value={rule.freq}
          onChange={(e) => setFreq(e.target.value as RecurrenceFreq)}
          className={SELECT_CLS}
          aria-label={i18n._(msg`Frequenza`)}
        >
          {FREQ_UNITS.map((f) => (
            <option key={f} value={f}>
              {unitLabel(f)}
            </option>
          ))}
        </StyledSelect>
      </div>

      {/* Weekday chips (weekly only) */}
      {rule.freq === "WEEKLY" ? (
        <div className="flex items-center gap-1">
          {narrowDays.map((d) => {
            const on = rule.byDay.includes(d.code);
            return (
              <button
                key={d.code}
                type="button"
                onClick={() => toggleDay(d.code)}
                aria-pressed={on}
                className={cn(
                  "h-6 w-6 rounded-full text-[11px] font-medium uppercase transition-colors",
                  on
                    ? "bg-ando-orange text-white"
                    : "border border-hairline text-ink-2 hover:border-ando-orange/40",
                )}
              >
                {d.label}
              </button>
            );
          })}
        </div>
      ) : null}

      {/* End */}
      <div className="flex flex-col gap-1.5 border-hairline border-t pt-2">
        <span className="text-[11px] text-ink-3">{i18n._(msg`Fine`)}</span>
        <label className="flex items-center gap-2 text-[12px] text-ink-1">
          <input
            type="radio"
            name="rec-end"
            checked={rule.endKind === "never"}
            onChange={() => setEndKind("never")}
            className="accent-ando-orange"
          />
          {i18n._(msg`Mai`)}
        </label>
        <label className="flex items-center gap-2 text-[12px] text-ink-1">
          <input
            type="radio"
            name="rec-end"
            checked={rule.endKind === "until"}
            onChange={() => setEndKind("until")}
            className="accent-ando-orange"
          />
          {i18n._(msg`Il`)}
          <input
            type="date"
            disabled={rule.endKind !== "until"}
            value={rule.until ?? toYmd(anchor)}
            onChange={(e) => patch({ endKind: "until", until: e.target.value })}
            className={cn(SELECT_CLS, "disabled:opacity-50")}
            aria-label={i18n._(msg`Data di fine`)}
          />
        </label>
        <label className="flex items-center gap-2 text-[12px] text-ink-1">
          <input
            type="radio"
            name="rec-end"
            checked={rule.endKind === "count"}
            onChange={() => setEndKind("count")}
            className="accent-ando-orange"
          />
          {i18n._(msg`Dopo`)}
          <input
            type="number"
            min={1}
            disabled={rule.endKind !== "count"}
            value={rule.count ?? 10}
            onChange={(e) =>
              patch({ endKind: "count", count: Math.max(1, Number.parseInt(e.target.value, 10) || 1) })
            }
            className={cn(NUM_CLS, "disabled:opacity-50")}
            aria-label={i18n._(msg`Numero di occorrenze`)}
          />
          {i18n._(msg`occorrenze`)}
        </label>
      </div>

      <div className="flex justify-end pt-1">
        <button
          type="button"
          onClick={onDone}
          className="rounded-md bg-ando-orange px-3 py-1 text-[12px] font-medium text-white transition-colors hover:bg-ando-orange/90"
        >
          {i18n._(msg`Fatto`)}
        </button>
      </div>
    </div>
  );
};
